import './App.css';
import UnknownPaymentResult from './Common/components/UnknownPaymentResult';
import { lazy, Suspense, useEffect, useState } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';

import { ToastProvider, useToast } from './Common/context/ToastContext';
import { TooltipProvider } from './Common/context/TooltipContext';
import MainLayout from './Common/components/Layout/MainLayout';
import ProtectedRoute from './Common/components/ProtectedRoute';
import OtpGateRoute from './Common/components/OtpGateRoute';
import {
  ADMIN_CP_PAYOUT_TENANT_ENABLED,
  ADMIN_SINGLE_CP_MODE,
  ADMIN_GNP_SHINSUNG_SYNC_ENABLED,
  ADMIN_TERMINAL_PHARMACY_IMPORT_ENABLED,
  ADMIN_GASYSTEM_CP_IMPORT_ENABLED,
  ADMIN_CHECKOUT_ENABLED,
  ADMIN_SMS_NOTIFICATION_ENABLED,
  getAdminMenu,
  getMerchantMenu,
  GASYSTEM_CP_PAYMENT_RESULTS_ENABLED,
  GASYSTEM_PAYMENT_RESULTS_ENABLED,
  MERCHANT_GASYSTEM_CUSTOMERS_ENABLED,
  MERCHANT_GASYSTEM_TERMINAL_IMPORT_ENABLED,
  MERCHANT_MANUAL_PAYMENT_ENABLED,
  MERCHANT_PAYMENT_LINKS_ENABLED,
  MERCHANT_PHARMACY_ASSIGNMENT_ENABLED,
  APP_MENU,
} from './Common/config/menuConfig';
import { BRAND_VARIANT, LOGIN_LAYOUT, SERVICE_BRAND_NAME } from './Common/config/brandConfig';

// 관리자 페이지
import AdminLogin from './Admin/Pages/Login/AdminLogin';
import AdminSettlement from './Admin/Pages/Settlement_1';
// import AdminSettlement from './Admin/Pages/Settlement';
// 
import AdminMerchantManage from './Admin/Pages/MerchantManage_2';
import AdminMerchantManage_1 from './Admin/Pages/MerchantManage_1';
// import AdminMerchantManage from './Admin/Pages/MerchantManage';

import AdminPaymentList from './Admin/Pages/PaymentList';
import AdminDashboard from './Admin/Pages/Dashboard';
import AdminDeposits from './Admin/Pages/Deposits';
import AdminTaxInvoice from './Admin/Pages/TaxInvoice_1';
import AdminPharmacyManage from './Admin/Pages/PharmacyManage';
import AdminSingleCpManage from './Admin/Pages/SingleCpManage';
import AdminSmsHistory from './Admin/Pages/SmsHistory';
import AdminStatusLog from './Admin/Pages/StatusLog';
import AdminGnpShinsungSync from './Admin/Pages/GnpShinsungSync';
import { getTerminalPharmacyImportCapability } from './Admin/api/terminalPharmacyImport';
import { getGasystemCpImportCapability } from './Admin/api/gasystemCpImport';
import { getGasystemCpCustomersCapability } from './Merchant/api/gasystemCustomers';
import { getGasystemTerminalImportCapability } from './Merchant/api/gasystemTerminalImport';
import { getCpPaymentLinkConfig } from './Merchant/api/paymentLinks';

// 가맹점 페이지
import MerchantLogin from './Merchant/Pages/Login/MerchantLogin';
import MerchantDashboard from './Merchant/Pages/Dashboard';
import MerchantPaymentList from './Merchant/Pages/PaymentList';
import MerchantPharmacyManage from './Merchant/Pages/PharmacyAssignmentManage';
import AdminTerminalPharmacyImport from './Admin/Pages/TerminalPharmacyImport';
import MerchantDeliveryManage from './Merchant/Pages/DeliveryManage';
import MerchantSettlementCalendar from './Merchant/Pages/SettlementCalendar';
import MerchantIntegratedSettlement from './Merchant/Pages/IntegratedSettlement';
import MerchantSugi from './Merchant/Pages/sugi';
import MerchantPaymentPage from './Merchant/Pages/AppPaymentPage';

// 앱(영업자) 페이지
import AppLogin from './App/Pages/Login/AppLogin';
import AppDashboardPage from './App/Pages/AppDashboardPage';
import AppPaymentPage from './App/Pages/AppPaymentPage';
import AppPaymentList from './App/Pages/AppPaymentList';
import AppSettings from './App/Pages/AppSettings';
import AppReceiptPublic from './App/Pages/AppReceiptPublic';

const AdminGasystemCpImport = lazy(() => import('./Admin/Pages/GasystemCpImport'));
const AdminGasystemPaymentResults = lazy(() => import('./Admin/Pages/GasystemPaymentResults'));
const AdminCheckoutOperations = lazy(() => import('./Admin/Pages/CheckoutOperations'));
const MerchantGasystemCustomers = lazy(() => import('./Merchant/Pages/GasystemCustomers'));
const MerchantGasystemPaymentResults = lazy(() => import('./Merchant/Pages/GasystemPaymentResults'));
const MerchantGasystemTerminalImport = lazy(() => import('./Merchant/Pages/GasystemTerminalImport'));
const MerchantPaymentLinks = lazy(() => import('./Merchant/Pages/PaymentLinks'));
const AppPaymentLinkPublic = lazy(() => import('./App/Pages/AppPaymentLinkPublic'));

function AppContent() {
    // TEST 값만 바꾸면 env 재시작 없이 화면 확인 가능(빌드에는 null로 두세요).
    const TEST_MERCHANT_PAGE_VERSION = null; // 0 or 1
    const merchantPageVersion =
        TEST_MERCHANT_PAGE_VERSION != null
            ? Number(TEST_MERCHANT_PAGE_VERSION)
            : Number(process.env.REACT_APP_MERCHANT_PAGE_VERSION || '1');
    const baseMerchantMenuItems = getMerchantMenu(merchantPageVersion, false, false, false);
    const isNtossBrand = ['default', 'ntoss'].includes(String(BRAND_VARIANT || '').trim().toLowerCase());
    const merchantDeliveryEnabled = baseMerchantMenuItems.some((item) => item.path === '/delivery');
    const merchantPaymentsEnabled = baseMerchantMenuItems.some((item) => item.path === '/payments');
    const merchantSettlementCalendarEnabled = baseMerchantMenuItems.some((item) => item.path === '/settlement-calendar');
    const { pathname } = useLocation();
    const navigate = useNavigate();
    const { showToast } = useToast();
    const MyData = useSelector((state) => state.MyData);
    const loginState = useSelector((state) => state.login);
    const [adminExcelEnabled, setAdminExcelEnabled] = useState(ADMIN_TERMINAL_PHARMACY_IMPORT_ENABLED);
    const [gasystemCpImportCapability, setGasystemCpImportCapability] = useState(null);
    const [gasystemCpImportCapabilityLoading, setGasystemCpImportCapabilityLoading] = useState(ADMIN_GASYSTEM_CP_IMPORT_ENABLED);
    const gasystemCpImportEnabled = ADMIN_GASYSTEM_CP_IMPORT_ENABLED && gasystemCpImportCapability?.enabled === true;
    const [gasystemTerminalImportCapability, setGasystemTerminalImportCapability] = useState(null);
    const [gasystemTerminalImportCapabilityLoading, setGasystemTerminalImportCapabilityLoading] = useState(MERCHANT_GASYSTEM_TERMINAL_IMPORT_ENABLED);
    const gasystemTerminalImportEnabled = MERCHANT_GASYSTEM_TERMINAL_IMPORT_ENABLED && gasystemTerminalImportCapability?.enabled === true;
    const [gasystemCustomersCapability, setGasystemCustomersCapability] = useState(null);
    const [gasystemCustomersCapabilityLoading, setGasystemCustomersCapabilityLoading] = useState(MERCHANT_GASYSTEM_CUSTOMERS_ENABLED);
    const gasystemCustomersEnabled = MERCHANT_GASYSTEM_CUSTOMERS_ENABLED && gasystemCustomersCapability?.enabled === true;
    const [paymentLinkConfig, setPaymentLinkConfig] = useState(null);
    const [paymentLinkConfigLoading, setPaymentLinkConfigLoading] = useState(MERCHANT_PAYMENT_LINKS_ENABLED);
    const paymentLinksEnabled = MERCHANT_PAYMENT_LINKS_ENABLED && paymentLinkConfig?.enabled === true;
    const merchantMenuItems = getMerchantMenu(
        merchantPageVersion,
        gasystemTerminalImportEnabled,
        gasystemCustomersEnabled,
        GASYSTEM_CP_PAYMENT_RESULTS_ENABLED,
        paymentLinksEnabled,
    );
    const adminMenuItems = getAdminMenu(adminExcelEnabled, gasystemCpImportEnabled, GASYSTEM_PAYMENT_RESULTS_ENABLED);
    const serviceBrandName = SERVICE_BRAND_NAME;
    const merchantHomePath = ADMIN_SINGLE_CP_MODE && merchantDeliveryEnabled ? '/cp/delivery' : '/cp/integrated-settlement';
    const isPhpPayLoginLayout = LOGIN_LAYOUT === 'phpay-split';
    const adminUserLabel = '관리자님';
    // 가맹점 로그인 응답의 name(cp_id) 형식으로 헤더에 표시 (없으면 '가맹점')
    const merchantUserLabel =
        MyData && typeof MyData === 'object' && !Array.isArray(MyData) && (MyData.name || MyData.cpId)
            ? `${MyData.name || ''}(${MyData.cpId || ''})`
            : '가맹점';

    const appUserLabel =
        MyData && typeof MyData === 'object' && !Array.isArray(MyData) && (MyData.name || MyData.cpId)
            ? `${MyData.name || ''}(${MyData.cpId || ''})`
            : '';

    const host = typeof window !== 'undefined' ? String(window.location.hostname || '').toLowerCase() : '';
    const isAdminDomain = host.includes('admin');
    const isMerchantDomain = host.includes('cp');
    useEffect(() => {
        if (!isAdminDomain || !loginState.value || !loginState.otpVerified) {
            setAdminExcelEnabled(ADMIN_TERMINAL_PHARMACY_IMPORT_ENABLED);
            return undefined;
        }

        let cancelled = false;
        getTerminalPharmacyImportCapability()
            .then((value) => {
                if (cancelled) return;
                const data = value?.terminalPharmacyImport ?? value ?? {};
                const source = String(
                    data.terminalPharmacySource ??
                    data.terminal_pharmacy_source ??
                    process.env.REACT_APP_TERMINAL_PHARMACY_SOURCE ??
                    'DISABLED'
                ).toUpperCase();
                const rawEnabled = data.excelImportEnabled ?? data.excel_import_enabled;
                const enabled = rawEnabled === undefined
                    ? ADMIN_TERMINAL_PHARMACY_IMPORT_ENABLED
                    : ['1', 'true', 'yes', 'y'].includes(String(rawEnabled).toLowerCase());
                setAdminExcelEnabled(source === 'EXCEL' && enabled);
            })
            .catch(() => {
                if (!cancelled) setAdminExcelEnabled(ADMIN_TERMINAL_PHARMACY_IMPORT_ENABLED);
            });
        return () => { cancelled = true; };
    }, [isAdminDomain, loginState.value, loginState.otpVerified]);

    useEffect(() => {
        if (!ADMIN_GASYSTEM_CP_IMPORT_ENABLED || !isAdminDomain || !loginState.value || !loginState.otpVerified) {
            setGasystemCpImportCapability(null);
            setGasystemCpImportCapabilityLoading(false);
            return undefined;
        }

        let cancelled = false;
        setGasystemCpImportCapabilityLoading(true);
        getGasystemCpImportCapability()
            .then((value) => {
                if (!cancelled) setGasystemCpImportCapability(value ?? null);
            })
            .catch(() => {
                if (!cancelled) setGasystemCpImportCapability(null);
            })
            .finally(() => {
                if (!cancelled) setGasystemCpImportCapabilityLoading(false);
            });
        return () => { cancelled = true; };
    }, [isAdminDomain, loginState.value, loginState.otpVerified]);

    useEffect(() => {
        if (!MERCHANT_GASYSTEM_TERMINAL_IMPORT_ENABLED || !isMerchantDomain || !loginState.value || !loginState.otpVerified) {
            setGasystemTerminalImportCapability(null);
            setGasystemTerminalImportCapabilityLoading(false);
            return undefined;
        }

        let cancelled = false;
        setGasystemTerminalImportCapabilityLoading(true);
        getGasystemTerminalImportCapability()
            .then((value) => {
                if (!cancelled) setGasystemTerminalImportCapability(value ?? null);
            })
            .catch(() => {
                if (!cancelled) setGasystemTerminalImportCapability(null);
            })
            .finally(() => {
                if (!cancelled) setGasystemTerminalImportCapabilityLoading(false);
            });
        return () => { cancelled = true; };
    }, [isMerchantDomain, loginState.value, loginState.otpVerified]);

    useEffect(() => {
        if (!MERCHANT_GASYSTEM_CUSTOMERS_ENABLED || !isMerchantDomain || !loginState.value || !loginState.otpVerified) {
            setGasystemCustomersCapability(null);
            setGasystemCustomersCapabilityLoading(false);
            return undefined;
        }

        let cancelled = false;
        setGasystemCustomersCapabilityLoading(true);
        getGasystemCpCustomersCapability()
            .then((value) => {
                if (!cancelled) setGasystemCustomersCapability(value ?? null);
            })
            .catch(() => {
                if (!cancelled) setGasystemCustomersCapability(null);
            })
            .finally(() => {
                if (!cancelled) setGasystemCustomersCapabilityLoading(false);
            });
        return () => { cancelled = true; };
    }, [isMerchantDomain, loginState.value, loginState.otpVerified]);

    useEffect(() => {
        if (!MERCHANT_PAYMENT_LINKS_ENABLED || !isMerchantDomain || !loginState.value || !loginState.otpVerified) {
            setPaymentLinkConfig(null);
            setPaymentLinkConfigLoading(false);
            return undefined;
        }

        let cancelled = false;
        setPaymentLinkConfigLoading(true);
        getCpPaymentLinkConfig()
            .then((value) => {
                if (!cancelled) setPaymentLinkConfig(value ?? null);
            })
            .catch(() => {
                if (!cancelled) setPaymentLinkConfig(null);
            })
            .finally(() => {
                if (!cancelled) setPaymentLinkConfigLoading(false);
            });
        return () => { cancelled = true; };
    }, [isMerchantDomain, loginState.value, loginState.otpVerified]);

    const isAppDomain = host.includes('deli');
    const domainBasePath = isAdminDomain ? '/admin' : isMerchantDomain ? '/cp' : isAppDomain ? '/app' : '/admin';

    // 도메인 기준으로 브라우저 타이틀 설정
    useEffect(() => {
        if (isAdminDomain) {
            document.title = `${serviceBrandName} 관리자`;
        } else if (isMerchantDomain) {
            document.title = `${serviceBrandName} 가맹점`;
        } else if (isAppDomain) {
            document.title = serviceBrandName;
        } else {
            document.title = serviceBrandName;
        }
    }, [serviceBrandName, isAdminDomain, isMerchantDomain, isAppDomain]);

    // 수기결제 팝업(/cp/sugi): 1920px 고정 해제, 전체 높이 사용 (팝업에서 폼이 보이도록)
    const isSugiPopup = pathname === '/cp/sugi';
    useEffect(() => {
        const html = document.documentElement;
        const body = document.body;
        if (isSugiPopup) {
            html.classList.add('sugi-popup-body');
            body.classList.add('sugi-popup-body');
        } else {
            html.classList.remove('sugi-popup-body');
            body.classList.remove('sugi-popup-body');
        }
        return () => {
            html.classList.remove('sugi-popup-body');
            body.classList.remove('sugi-popup-body');
        };
    }, [isSugiPopup]);

    const isAppMobile = isAppDomain && (pathname.startsWith('/app') || pathname === '/' || pathname === '/login');

    // 앱 모바일 화면: html, body에 클래스 추가하여 높이 제한
    useEffect(() => {
        const html = document.documentElement;
        const body = document.body;
        if (isAppMobile) {
            html.classList.add('app-mobile-body');
            body.classList.add('app-mobile-body');
        } else {
            html.classList.remove('app-mobile-body');
            body.classList.remove('app-mobile-body');
        }
        return () => {
            html.classList.remove('app-mobile-body');
            body.classList.remove('app-mobile-body');
        };
    }, [isAppMobile]);

    // PWA 설치용 상태 (앱 화면에서만 사용)
    const [deferredInstallPrompt, setDeferredInstallPrompt] = useState(null);
    const [showInstallBanner, setShowInstallBanner] = useState(false);
    // iOS 홈 화면 추가 안내 모달 상태 (배송기사 앱 화면에서만 사용)
    const [showIosInstallGuide, setShowIosInstallGuide] = useState(false);

    // 현재 환경이 iOS Safari인지 판별 (한 번 계산해 재사용)
    const ua = typeof window !== 'undefined' ? window.navigator.userAgent || '' : '';
    const isIOS = /iphone|ipad|ipod/i.test(ua);

    // 도메인별 기본 리다이렉트 경로 결정
    const getDefaultRedirect = () => {
        if (isAdminDomain) return '/admin/login';
        if (isMerchantDomain) return '/cp/login';
        if (isAppDomain) return '/app/login';
        return '/admin/login';
    };

    // 도메인과 prefix가 다른 경로 접근 시 도메인 기본 로그인으로 보정
    useEffect(() => {
        if (pathname.startsWith('/admin') && !isAdminDomain) {
            navigate(getDefaultRedirect(), { replace: true });
            return;
        }
        if (pathname.startsWith('/cp') && !isMerchantDomain) {
            navigate(getDefaultRedirect(), { replace: true });
            return;
        }
        if (pathname.startsWith('/app') && !isAppDomain) {
            navigate(getDefaultRedirect(), { replace: true });
        }
    }, [pathname, isAdminDomain, isMerchantDomain, isAppDomain, navigate]);

    // beforeinstallprompt 이벤트: 설치 가능 시점에 deferredInstallPrompt 저장 (Android)
    useEffect(() => {
        const p = (pathname.replace(/\/$/, '') || '/');
        const isApp = isAppDomain && (pathname.startsWith('/app') || p === '/' || p === '/login');
        const handler = (e) => {
            if (!isApp) return;
            if (isIOS) return;
            e.preventDefault();
            setDeferredInstallPrompt(e);
        };
        window.addEventListener('beforeinstallprompt', handler);
        return () => window.removeEventListener('beforeinstallprompt', handler);
    }, [pathname, isAppDomain]);

    // 앱 설치 완료 시 deferredInstallPrompt 초기화
    useEffect(() => {
        const onInstalled = () => setDeferredInstallPrompt(null);
        window.addEventListener('appinstalled', onInstalled);
        return () => window.removeEventListener('appinstalled', onInstalled);
    }, []);

    // 설치 배너 표시: iOS·Android 모두 로그인 페이지에서 계속 안내
    // - iOS: standalone이면 숨김. localStorage 설치 이력 있으면 숨김
    // - Android: 로그인 페이지에서는 standalone 포함 항상 표시 (설치 후 PWA로 열려도 배너 유지)
    useEffect(() => {
        if (typeof window === 'undefined') return;
        const isStandalone =
            window.matchMedia?.('(display-mode: standalone)').matches ||
            window.navigator.standalone === true;
        const path = pathname.replace(/\/$/, '') || '/';
        const isAppLoginPage = path === '/app' || path === '/app/login' || path === '/' || path === '/login';

        if (!isAppDomain || (!pathname.startsWith('/app') && path !== '/' && path !== '/login') || !isAppLoginPage) {
            setShowInstallBanner(false);
            setShowIosInstallGuide(false);
            return;
        }

        if (isIOS) {
            if (isStandalone) {
                try { window.localStorage.setItem('ios_app_installed', '1'); } catch (e) { }
                setShowInstallBanner(false);
                setShowIosInstallGuide(false);
                return;
            }
            const INSTALLED_KEY = 'ios_app_installed';
            const installed = (() => { try { return window.localStorage.getItem(INSTALLED_KEY) === '1'; } catch { return false; } })();
            setShowInstallBanner(!installed);
            if (installed) setShowIosInstallGuide(false);
        } else {
            // Android: 로그인 페이지에서는 항상 배너 표시
            setShowInstallBanner(true);
        }
    }, [pathname, isAppDomain]);

    const handleInstallClick = async () => {
        if (!deferredInstallPrompt) return;
        deferredInstallPrompt.prompt();
        try {
            await deferredInstallPrompt.userChoice;
        } finally {
            setDeferredInstallPrompt(null);
        }
    };

    const isAdminResponsive =
      /^\/admin\/(settlement|merchants|pharmacies|payments|sms|activity|status-logs|terminal-pharmacy-import|gasystem-cp-import|gasystem-payment-results|checkout|shinsung-sync|payout-management)(\/|$)/.test(pathname);
    return (
        <div className={`app-wrapper${isAppMobile ? ' app-mobile' : ''}${isSugiPopup ? ' sugi-popup' : ''}${isAdminResponsive ? ' admin-settlement-responsive' : ''}`}>
            <div className="App">
                <Routes>
                    {/* / → 관리자 로그인으로 리다이렉트 (없으면 가맹점 로그인) */}
                    <Route path="/" element={<Navigate to={domainBasePath} replace />} />

                    {/* 관리자: /admin, /admin/login → 로그인, /admin/otp → OTP / 그 외 → 메인 */}
                    <Route path="/admin">
                        <Route index element={<AdminLogin />} />
                        <Route path="login" element={<AdminLogin />} />
                        <Route path="otp" element={<OtpGateRoute loginPath="/admin/login" dashboardPath="/admin/settlement" userType="admin" />} />
                        <Route
                            element={
                                <ProtectedRoute loginPath="/admin/login">
                                    <MainLayout menuItems={adminMenuItems} basePath="/admin" userLabel={adminUserLabel} />
                                </ProtectedRoute>
                            }
                        >
                            <Route index element={<Navigate to="/admin/settlement" replace />} />
                            <Route path="settlement" element={<AdminSettlement />} />
                            <Route path="merchants" element={ADMIN_SINGLE_CP_MODE ? <AdminSingleCpManage /> : <AdminMerchantManage />} />
                            <Route path="merchants_1" element={isNtossBrand ? <AdminMerchantManage_1 /> : <Navigate to="/admin/merchants" replace />} />
                            <Route path="pharmacies" element={ADMIN_SINGLE_CP_MODE ? <Navigate to="/admin/merchants?tab=pharmacies" replace /> : <AdminPharmacyManage />} />
                            <Route path="payments" element={<AdminPaymentList />} />
                            <Route path="activity" element={ADMIN_SINGLE_CP_MODE ? <AdminStatusLog /> : <Navigate to="/admin/settlement" replace />} />
                            <Route path="status-logs" element={ADMIN_SINGLE_CP_MODE ? <Navigate to="/admin/activity" replace /> : <Navigate to="/admin/settlement" replace />} />
                            <Route path="sms" element={ADMIN_SMS_NOTIFICATION_ENABLED ? <AdminSmsHistory /> : <Navigate to="/admin/settlement" replace />} />
                            {ADMIN_GNP_SHINSUNG_SYNC_ENABLED && <Route path="shinsung-sync" element={<AdminGnpShinsungSync />} />}
                            {adminExcelEnabled && <Route path="terminal-pharmacy-import" element={<AdminTerminalPharmacyImport />} />}
                            <Route
                                path="gasystem-cp-import"
                                element={ADMIN_GASYSTEM_CP_IMPORT_ENABLED && gasystemCpImportCapabilityLoading
                                    ? <div className="flex min-h-[320px] items-center justify-center text-sm text-[#6b7684]">기능 설정을 확인하고 있습니다.</div>
                                    : gasystemCpImportEnabled
                                        ? (
                                            <Suspense fallback={<div className="flex min-h-[320px] items-center justify-center text-sm text-[#6b7684]">화면을 불러오고 있습니다.</div>}>
                                                <AdminGasystemCpImport capability={gasystemCpImportCapability} />
                                            </Suspense>
                                        )
                                        : <Navigate to="/admin/settlement" replace />}
                            />
                            <Route
                                path="gasystem-payment-results"
                                element={GASYSTEM_PAYMENT_RESULTS_ENABLED
                                    ? (
                                        <Suspense fallback={<div className="flex min-h-[320px] items-center justify-center text-sm text-[#6b7684]">화면을 불러오고 있습니다.</div>}>
                                            <AdminGasystemPaymentResults />
                                        </Suspense>
                                    )
                                    : <Navigate to="/admin/settlement" replace />}
                            />
                            <Route
                                path="checkout"
                                element={ADMIN_CHECKOUT_ENABLED
                                    ? (
                                        <Suspense fallback={<div className="flex min-h-[320px] items-center justify-center text-sm text-[#6b7684]">화면을 불러오고 있습니다.</div>}>
                                            <AdminCheckoutOperations />
                                        </Suspense>
                                    )
                                    : <Navigate to="/admin/settlement" replace />}
                            />
                            {ADMIN_CP_PAYOUT_TENANT_ENABLED && <Route path="payout-management" element={<Navigate to="/admin/merchants" replace />} />}
                            {/* <Route path="tax-invoice" element={<AdminTaxInvoice />} /> */}
                            {/* <Route path="dashboard" element={<AdminDashboard />} /> */}
                            {/* <Route path="deposits" element={<AdminDeposits />} /> */}
                        </Route>
                        {/* 관리자 경로가 없을 때 fallback */}
                        <Route path="*" element={<Navigate to={getDefaultRedirect()} replace />} />
                    </Route>

                    {/* 가맹점: /cp, /cp/login → 로그인, /cp/otp → OTP / 그 외 → 메인 */}
                    <Route path="/cp">
                        <Route index element={<MerchantLogin />} />
                        <Route path="login" element={<MerchantLogin />} />
                        <Route path="otp" element={<OtpGateRoute loginPath="/cp/login" dashboardPath={merchantHomePath} userType="merchant" />} />
                        {/* 수기결제: 가맹점 전용 (Merchant 폴더), 상단 UI A/B 토글, 헤더/사이드바 없이 팝업용 */}
                        <Route
                            path="sugi"
                            element={
                                MERCHANT_MANUAL_PAYMENT_ENABLED ? (
                                    <ProtectedRoute loginPath="/cp/login"><MerchantPaymentPage /></ProtectedRoute>
                                ) : (
                                    <Navigate to={merchantHomePath} replace />
                                )
                            }
                        />
                        <Route
                            element={
                                <ProtectedRoute loginPath="/cp/login">
                                    <MainLayout menuItems={merchantMenuItems} basePath="/cp" userLabel={merchantUserLabel} />
                                </ProtectedRoute>
                            }
                        >
                            <Route index element={<Navigate to={merchantHomePath} replace />} />
                            {/* 가맹점 대시보드는 현재 사용하지 않지만, 필요 시를 대비해 라우트만 유지 */}
                            <Route path="dashboard" element={<MerchantDashboard />} />
                            <Route path="payments" element={merchantPaymentsEnabled ? <MerchantPaymentList /> : <Navigate to={merchantHomePath} replace />} />
                            <Route path="integrated-settlement" element={<MerchantIntegratedSettlement />} />
                            <Route path="settlement-calendar" element={merchantSettlementCalendarEnabled ? <MerchantSettlementCalendar /> : <Navigate to={merchantHomePath} replace />} />
                            <Route path="pharmacies" element={MERCHANT_PHARMACY_ASSIGNMENT_ENABLED ? <MerchantPharmacyManage /> : <Navigate to={merchantHomePath} replace />} />
                            <Route path="delivery" element={merchantDeliveryEnabled ? <MerchantDeliveryManage /> : <Navigate to={merchantHomePath} replace />} />
                            <Route
                                path="gasystem-customers"
                                element={MERCHANT_GASYSTEM_CUSTOMERS_ENABLED && gasystemCustomersCapabilityLoading
                                    ? <div className="flex min-h-[320px] items-center justify-center text-sm text-[#6b7684]">기능 설정을 확인하고 있습니다.</div>
                                    : gasystemCustomersEnabled
                                        ? (
                                            <Suspense fallback={<div className="flex min-h-[320px] items-center justify-center text-sm text-[#6b7684]">화면을 불러오고 있습니다.</div>}>
                                                <MerchantGasystemCustomers />
                                            </Suspense>
                                        )
                                        : <Navigate to={merchantHomePath} replace />}
                            />
                            <Route
                                path="gasystem-payment-results"
                                element={GASYSTEM_CP_PAYMENT_RESULTS_ENABLED
                                    ? (
                                        <Suspense fallback={<div className="flex min-h-[320px] items-center justify-center text-sm text-[#6b7684]">화면을 불러오고 있습니다.</div>}>
                                            <MerchantGasystemPaymentResults />
                                        </Suspense>
                                    )
                                    : <Navigate to={merchantHomePath} replace />}
                            />
                            <Route
                                path="terminal-import"
                                element={MERCHANT_GASYSTEM_TERMINAL_IMPORT_ENABLED && gasystemTerminalImportCapabilityLoading
                                    ? <div className="flex min-h-[320px] items-center justify-center text-sm text-[#6b7684]">기능 설정을 확인하고 있습니다.</div>
                                    : gasystemTerminalImportEnabled
                                        ? (
                                            <Suspense fallback={<div className="flex min-h-[320px] items-center justify-center text-sm text-[#6b7684]">화면을 불러오고 있습니다.</div>}>
                                                <MerchantGasystemTerminalImport capability={gasystemTerminalImportCapability} />
                                            </Suspense>
                                        )
                                        : <Navigate to={merchantHomePath} replace />}
                            />
                            <Route
                                path="payment-links"
                                element={MERCHANT_PAYMENT_LINKS_ENABLED && paymentLinkConfigLoading
                                    ? <div className="flex min-h-[320px] items-center justify-center text-sm text-[#6b7684]">기능 설정을 확인하고 있습니다.</div>
                                    : paymentLinksEnabled
                                        ? (
                                            <Suspense fallback={<div className="flex min-h-[320px] items-center justify-center text-sm text-[#6b7684]">화면을 불러오고 있습니다.</div>}>
                                                <MerchantPaymentLinks initialConfig={paymentLinkConfig} />
                                            </Suspense>
                                        )
                                        : <Navigate to={merchantHomePath} replace />}
                            />
                        </Route>
                        {/* 가맹점 경로가 없을 때 fallback */}
                        <Route path="*" element={<Navigate to={getDefaultRedirect()} replace />} />
                    </Route>

                    {/* 앱: /app, /app/login → 로그인 / dashboard·payment·payment-list는 모바일 전체 화면(헤더·사이드바 없음) */}
                    <Route path="/app">
                        <Route index element={<AppLogin />} />
                        <Route path="login" element={<AppLogin />} />
                        {/* 전표 공개 조회: 로그인 없이 비밀번호로 접근 (카카오 알림톡 링크) */}
                        <Route path="receipt/:token" element={<AppReceiptPublic />} />
                        {MERCHANT_PAYMENT_LINKS_ENABLED && (
                            <Route
                                path="payment-link/:token"
                                element={
                                    <Suspense fallback={<div className="flex min-h-[100dvh] items-center justify-center text-sm text-[#6b7684]">결제 화면을 불러오고 있습니다.</div>}>
                                        <AppPaymentLinkPublic />
                                    </Suspense>
                                }
                            />
                        )}
                        <Route path="dashboard" element={<ProtectedRoute loginPath="/app/login"><AppDashboardPage /></ProtectedRoute>} />
                        <Route path="dashboard_1" element={<ProtectedRoute loginPath="/app/login"><AppDashboardPage /></ProtectedRoute>} />
                        <Route path="payment" element={<ProtectedRoute loginPath="/app/login"><AppPaymentPage /></ProtectedRoute>} />
                        <Route path="payment-list" element={<ProtectedRoute loginPath="/app/login"><AppPaymentList /></ProtectedRoute>} />
                        <Route path="settings" element={<ProtectedRoute loginPath="/app/login"><AppSettings /></ProtectedRoute>} />
                        {/* 앱 경로가 없을 때 fallback */}
                        <Route path="*" element={<Navigate to={getDefaultRedirect()} replace />} />
                    </Route>

                    {/* 모든 경로가 없을 때 기본 리다이렉트 */}
                    <Route path="*" element={<Navigate to={getDefaultRedirect()} replace />} />
                </Routes>
                {/* 앱(/app) 로그인 화면에서만 보이는 설치 배너 (Android: 설치, iOS: 안내 모달) */}
                {isAppDomain && isAppMobile && showInstallBanner && (() => { const p = (pathname.replace(/\/$/, '') || '/'); return p === '/app' || p === '/app/login' || p === '/' || p === '/login'; })() && (
                    <>
                        <div className={isPhpPayLoginLayout
                            ? 'fixed left-0 right-0 bottom-3 z-[2000] flex justify-center px-4'
                            : 'fixed left-0 right-0 bottom-4 z-[2000] flex justify-center px-4'}>
                            <div className={isPhpPayLoginLayout
                                ? 'w-full max-w-sm bg-[#f2f4f6] text-[#333d4b] rounded-2xl px-4 py-3 flex items-center justify-between gap-3'
                                : 'w-full max-w-sm bg-[#191f28] text-white rounded-2xl shadow-xl px-4 py-3 flex items-center justify-between gap-3'}>
                                <div className="flex flex-col">
                                    <span className="text-[14px] font-semibold">
                                        {isPhpPayLoginLayout ? 'phPay 앱으로 더 편리하게' : '앱을 설치할 수 있어요'}
                                    </span>
                                    {!isPhpPayLoginLayout && (
                                        <span className="text-[12px] text-[#cbd5f5]">홈 화면에 추가해서 앱처럼 사용하세요.</span>
                                    )}
                                </div>
                                <button
                                    type="button"
                                    onClick={() => {
                                        const ua = typeof window !== 'undefined' ? window.navigator.userAgent || '' : '';
                                        const isIOS = /iphone|ipad|ipod/i.test(ua);
                                        if (isIOS) {
                                            setShowIosInstallGuide(true);
                                        } else {
                                            if (deferredInstallPrompt) {
                                                handleInstallClick();
                                            } else {
                                                showToast('이미 설치되어있습니다. 없다면 새로고침 후 다시 시도해 주세요.');
                                            }
                                        }
                                    }}
                                    className={isPhpPayLoginLayout
                                        ? 'px-3 py-1.5 bg-white text-[var(--brand-primary)] rounded-xl text-[13px] font-bold'
                                        : 'px-3 py-1.5 bg-white text-[#191f28] rounded-xl text-[13px] font-bold'}
                                >
                                    {(() => {
                                        const ua = typeof window !== 'undefined' ? window.navigator.userAgent || '' : '';
                                        const isIOS = /iphone|ipad|ipod/i.test(ua);
                                        return isIOS ? '안내' : '설치';
                                    })()}
                                </button>
                            </div>
                        </div>
                        {showIosInstallGuide && (
                            <div className="fixed inset-0 z-[2100] flex items-center justify-center bg-black/40 px-4">
                                <div className="w-full max-w-sm bg-white rounded-2xl shadow-2xl overflow-hidden">
                                    <div className="px-5 py-4 border-b border-[#e5e7eb] flex items-center justify-between">
                                        <h2 className="text-[16px] font-bold text-[#111827]">iOS 설치 안내</h2>
                                        {/* <button
                                                type="button"
                                                onClick={() => setShowIosInstallGuide(false)}
                                                className="text-[#9ca3af] hover:text-[#111827] text-[18px] leading-none px-1"
                                            >
                                                ×
                                            </button> */}
                                    </div>
                                    <div className="px-5 py-4 space-y-3 text-[13px] text-[#374151]">
                                        <p className="text-[13px] text-[#4b5563]">
                                            iPhone / iPad에서 앱을 홈 화면에 추가하면, 설치된 앱처럼 사용할 수 있습니다.
                                        </p>
                                        <ol className="list-decimal list-inside space-y-2">
                                            <li>화면 하단의 <span className="font-bold">공유(Share)</span> 버튼을 누릅니다.</li>
                                            <li>공유 시트에서 <span className="font-bold">더보기</span>를 눌러 메뉴를 펼칩니다.</li>
                                            <li>목록에서 <span className="font-bold">홈 화면에 추가</span> 메뉴를 선택합니다.</li>
                                            <li>이름을 확인한 뒤 <span className="font-bold">추가</span> 버튼을 눌러주세요.</li>
                                        </ol>
                                        <p className="text-[12px] text-[#6b7280]">
                                            이렇게 추가한 아이콘으로 실행하면, 앱처럼 사용할 수 있습니다.
                                        </p>
                                    </div>
                                    <div className="px-5 py-3 border-t border-[#e5e7eb] flex justify-end">
                                        <button
                                            type="button"
                                            onClick={() => setShowIosInstallGuide(false)}
                                            className="px-4 py-2 rounded-xl text-[13px] font-bold bg-[#111827] text-white"
                                        >
                                            확인
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}

function App() {
    return (
        <ToastProvider>
            <TooltipProvider>
                <AppContent /><UnknownPaymentResult />
            </TooltipProvider>
        </ToastProvider>
    );
}

export default App;
